import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram, Facebook, Youtube } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { useData } from '../context/DataContext';
import { contactInfo } from '../mock';

export default function Footer() {
  const { lang, t } = useLang();
  const { settings } = useData();
  const social = settings.social || {};
  const phone = settings.contactPhone || contactInfo.phone;
  const email = settings.contactEmail || contactInfo.email;
  const address = settings.contactAddress || contactInfo.address;
  const T = {
    tagline: { en: 'A Sacred Bridge Connecting Devotees with the Divine', hi: 'भक्तों को ईश्वर से जोड़ने वाला पवित्र सेतु' },
    quick: { en: 'Quick Links', hi: 'त्वरित लिंक' },
    contact: { en: 'Contact Us', hi: 'संपर्क' },
    follow: { en: 'Follow Us', hi: 'हमें फ़ॉलो करें' },
    rights: { en: 'All rights reserved', hi: 'सर्वाधिकार सुरक्षित' }
  };

  return (
    <footer className="mt-20 wine-dark pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img src="/brand/logo.png" alt="Navavidha Bhakti" className="w-24 h-24 md:w-28 md:h-28 object-contain drop-shadow-[0_4px_16px_rgba(233,167,86,0.4)]" />
            <div>
              <div className="text-xl md:text-2xl font-bold gradient-text-on-dark uppercase tracking-wider">Navavidha</div>
              <div className="text-[11px] md:text-xs uppercase tracking-[0.3em] text-nv-gold-light/85">Bhakti</div>
            </div>
          </div>
          <p className="text-sm text-nv-cream/70 leading-relaxed devnag mb-2">|| नवधा भक्ति, नवधा कृपा ||</p>
          <p className="text-sm text-nv-cream/70 leading-relaxed">{t(T.tagline)}</p>
        </div>

        <div>
          <h4 className="text-nv-gold-light font-semibold mb-4 uppercase tracking-wider text-sm">{t(T.quick)}</h4>
          <ul className="space-y-2 text-sm text-nv-cream/80">
            <li><Link to="/" className="hover:text-nv-gold-light">{lang === 'en' ? 'Home' : 'होम'}</Link></li>
            <li><Link to="/upcoming-pujas" className="hover:text-nv-gold-light">{lang === 'en' ? 'Upcoming Pujas' : 'आगामी पूजा'}</Link></li>
            <li><Link to="/about" className="hover:text-nv-gold-light">{lang === 'en' ? 'About Us' : 'हमारे बारे में'}</Link></li>
            <li><Link to="/contact" className="hover:text-nv-gold-light">{lang === 'en' ? 'Contact Us' : 'संपर्क'}</Link></li>
            <li><Link to="/login" className="hover:text-nv-gold-light">{lang === 'en' ? 'Devotee Login' : 'भक्त लॉगिन'}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-nv-gold-light font-semibold mb-4 uppercase tracking-wider text-sm">{lang === 'en' ? 'Legal' : 'नियम-कानून'}</h4>
          <ul className="space-y-2 text-sm text-nv-cream/80">
            <li><Link to="/privacy-policy" className="hover:text-nv-gold-light">{lang === 'en' ? 'Privacy Policy' : 'गोपनीयता नीति'}</Link></li>
            <li><Link to="/terms-and-conditions" className="hover:text-nv-gold-light">{lang === 'en' ? 'Terms & Conditions' : 'नियम एवं शर्तें'}</Link></li>
            <li><Link to="/refund-policy" className="hover:text-nv-gold-light">{lang === 'en' ? 'Refund & Cancellation' : 'रिफंड नीति'}</Link></li>
            <li><Link to="/shipping-policy" className="hover:text-nv-gold-light">{lang === 'en' ? 'Shipping & Delivery' : 'शिपिंग नीति'}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-nv-gold-light font-semibold mb-4 uppercase tracking-wider text-sm">{t(T.contact)}</h4>
          <ul className="space-y-3 text-sm text-nv-cream/80">
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-nv-gold" /> {address}</li>
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-nv-gold" /> <a href={`tel:${phone}`} className="hover:text-nv-gold-light">{phone}</a></li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-nv-gold" /> <a href={`mailto:${email}`} className="hover:text-nv-gold-light break-all">{email}</a></li>
          </ul>
          <div className="flex gap-3 mt-5">
            {social.instagram && <a href={social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-full bg-white/10 hover:bg-nv-orange flex items-center justify-center transition"><Instagram className="w-4 h-4" /></a>}
            {social.facebook && <a href={social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-full bg-white/10 hover:bg-nv-orange flex items-center justify-center transition"><Facebook className="w-4 h-4" /></a>}
            {social.youtube && <a href={social.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" className="w-9 h-9 rounded-full bg-white/10 hover:bg-nv-orange flex items-center justify-center transition"><Youtube className="w-4 h-4" /></a>}
            {!social.instagram && !social.facebook && !social.youtube && (
              <span className="text-xs text-nv-cream/50">{lang === 'en' ? 'Coming soon' : 'जल्द ही'}</span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-10 pt-6 border-t border-nv-gold/20 flex flex-col md:flex-row justify-between gap-4 text-xs text-nv-cream/60">
        <p>© {new Date().getFullYear()} Navavidha Bhakti. {t(T.rights)} 🇮🇳</p>
        <p className="flex items-center gap-2">Made with devotion in Bharat · <Link to="/admin/login" className="hover:text-nv-gold-light">Admin</Link></p>
      </div>
    </footer>
  );
}
